import { inject, Injectable } from '@angular/core';
import { Recording } from 'src/api/models';
import { WS_STATUS_RESTORED } from 'src/app/app.constants';
import { getNextPingPongTime } from 'src/app/shared/utils/date.utils';
import { WSEntity, WSMessage, WSStatus } from 'src/app/shared/enums/ws.enums';
import { WsMessageModel } from 'src/app/shared/models/ws.models';
import { AnnotationListsStore } from '../store/annotation-lists.store';
import { AnnotationsStore } from '../store/annotations.store';
import { CurrentSelectionsStore } from '../store/current-selections.store';
import { PlaylistsStore } from '../store/playlists.store';
import { RecordersStore } from '../store/recorders.store';
import { RecordingStore } from '../store/recording.store';
import { RecordingListStore } from '../store/recording-list.store';
import { TrashStore } from '../store/trash.store';
import { WsStore } from '../store/ws.store';

/**
 * Fans incoming server events out to the signal stores.
 *
 * Split from WsService so that the transport (staying connected, ping/pong,
 * re-subscribing per route) and the routing (which store learns about which
 * entity) can be read and tested apart — this half is what grows every time
 * the server learns to push a new kind of message.
 *
 * Note that refreshing the ping-pong timer is deliberately per-branch rather
 * than done once up front: several message kinds (annotation lists, thumbnails,
 * trash, batch deletes) do not touch it today, and hoisting it would quietly
 * change when the next PING is sent.
 */
@Injectable({ providedIn: 'root' })
export class WsMessageRouter {
  private readonly wsStore = inject(WsStore);
  private readonly recordersStore = inject(RecordersStore);
  private readonly recordingStore = inject(RecordingStore);
  private readonly recordingListStore = inject(RecordingListStore);
  private readonly annotationsStore = inject(AnnotationsStore);
  private readonly annotationListsStore = inject(AnnotationListsStore);
  private readonly playlistsStore = inject(PlaylistsStore);
  private readonly currentSelectionsStore = inject(CurrentSelectionsStore);
  private readonly trashStore = inject(TrashStore);

  resolveWsMessage(msg: WsMessageModel): void {
    switch (msg.msg) {
      case WSMessage.PONG:
        return this.resolveWsPongMessage();
      case WSMessage.REFRESH:
        return this.resolveWsRefreshMessage(msg);
      case WSMessage.DELETED:
        return this.resolveWsDeleteMessage(msg);
      case WSMessage.CREATED:
        return this.resolveWsCreatedMessage(msg);
      case WSMessage.CHANGE_SHARING:
        return this.resolveWsChangeSharing(msg);
      default:
        return;
    }
  }
  resolveWsPongMessage(): void {
    this.wsStore.pingPongUpdate(getNextPingPongTime());
  }
  resolveWsCreatedMessage(msg: WsMessageModel): void {
    if (msg.entity === WSEntity.PLAYLIST && msg.id) {
      this.playlistsStore.handleCreatedPlaylist(msg.id);
      this.wsStore.pingPongUpdate(getNextPingPongTime());
    }
  }
  resolveWsRefreshMessage(msg: WsMessageModel): void {
    switch (msg.entity) {
      case WSEntity.PLAYLIST:
        this.resolveWsRefreshPlaylistMessage(msg);
        break;
      case WSEntity.ANNOTATION:
        this.resolveWsRefreshAnnotationMessage(msg);
        break;
      case WSEntity.ANNOTATION_LIST:
        this.resolveWsRefreshAnnotationListMessage(msg);
        break;
      case WSEntity.RECORDING:
        this.resolveWsRefreshRecordingMessage(msg);
        break;
      case WSEntity.REMUX:
        this.resolveWsRefreshRemuxMessage(msg);
        break;
      case WSEntity.TRASH:
        this.trashStore.refresh();
        break;
      case WSEntity.THUMBNAIL:
        this.resolveWsRefreshThumbnailMessage(msg);
        break;
    }
  }
  resolveWsRefreshPlaylistMessage(msg: WsMessageModel): void {
    this.playlistsStore.refreshPlaylist(
      msg.id as string,
      msg.status !== WSStatus.NONE,
      msg.status || WSStatus.NONE,
      msg.userId,
    );
    this.wsStore.pingPongUpdate(getNextPingPongTime());
  }
  resolveWsRefreshAnnotationMessage(msg: WsMessageModel): void {
    if (msg.id && msg.recordingId) {
      this.annotationListsStore.refreshRecordingsAnnotationList({
        recordingId: msg.recordingId,
      });
      this.wsStore.pingPongUpdate(getNextPingPongTime());
      this.annotationsStore.pollAnnotations(
        msg.id,
        getNextPingPongTime(),
        msg.status,
      );
      if (msg.status && msg.userId) {
        this.annotationsStore.showChangeAnnotationNotification(
          msg.userId,
          msg.status,
        );
      }
    }
  }
  resolveWsRefreshAnnotationListMessage(msg: WsMessageModel): void {
    if (msg.id) {
      this.annotationListsStore.refreshAnnotationList(
        msg.id,
        msg.status || WSStatus.NONE,
        msg.ids,
      );
    }
  }
  resolveWsRefreshRecordingMessage(msg: WsMessageModel): void {
    if (msg.id) {
      this.wsStore.pingPongUpdate(getNextPingPongTime());
      this.recordingListStore.refreshById({
        id: msg.id,
        refreshMetadata: false,
      });

      this.recordersStore.refreshByIds(msg.recordersIds ?? []);

      if (msg.status === WS_STATUS_RESTORED) {
        this.trashStore.handleRestored(<Recording>{ id: msg.id });
        this.recordingListStore.handleRestored(<Recording>{ id: msg.id });
        this.playlistsStore.refreshPlaylistsByRecordingId(msg.id);
      }
    }
  }
  resolveWsRefreshRemuxMessage(msg: WsMessageModel): void {
    if (msg.recordingId) {
      this.currentSelectionsStore.reactOnRemuxWs(
        msg.status,
        msg.userId,
        msg.id,
      );
      this.wsStore.pingPongUpdate(getNextPingPongTime());
      this.recordingListStore.refreshById({
        id: msg.recordingId,
        refreshMetadata: false,
        remuxInfo: {
          videoId: msg.id,
          userId: msg.userId,
          status: msg.status || WSStatus.NONE,
        },
      });
    }
  }
  resolveWsRefreshThumbnailMessage(msg: WsMessageModel): void {
    if (msg.id && msg.url) {
      if (msg.playlistId) {
        this.playlistsStore.refreshPlaylistAnnotationThumbnail(
          msg.id,
          msg.playlistId,
          msg.url,
        );
      } else {
        this.annotationsStore.refreshAnnotationThumbnail(msg.id, msg.url);
      }
    }
  }
  resolveWsChangeSharing(msg: WsMessageModel): void {
    if (msg.entity === WSEntity.RECORDING && msg.id) {
      this.wsStore.pingPongUpdate(getNextPingPongTime());
      this.recordingListStore.refreshById({
        id: msg.id,
        refreshMetadata: true,
      });
      this.playlistsStore.refreshPlaylistsByRecordingId(msg.id);
      return;
    }

    if (msg.entity === WSEntity.PLAYLIST && msg.id) {
      this.playlistsStore.get();
      this.playlistsStore.refreshPlaylist(msg.id, false, WSStatus.NONE);
      this.wsStore.pingPongUpdate(getNextPingPongTime());
      return;
    }
  }
  resolveWsDeleteMessage(msg: WsMessageModel): void {
    if (msg.entity === WSEntity.RECORDING && msg.id) {
      this.recordingStore.handleDeletedRecording(msg.id);
      this.wsStore.pingPongUpdate(getNextPingPongTime());
      this.playlistsStore.refreshPlaylistsByRecordingId(msg.id);

      this.recordersStore.refreshByIds(msg.recordersIds ?? []);
    }

    if (msg.entity === WSEntity.PLAYLIST && msg.id) {
      this.playlistsStore.handleDeletedPlaylist(msg.id);
      this.wsStore.pingPongUpdate(getNextPingPongTime());
    }

    if (msg.entity === WSEntity.RECORDINGS && msg.ids) {
      this.trashStore.refresh();
      this.recordingStore.handleDeletedRecordings(msg.ids);
      this.playlistsStore.refreshPlaylistsByRecordingsIds(msg.ids);

      this.recordersStore.refreshByIds(msg.recordersIds ?? []);
    }
  }
}
